import React, { useContext } from "react";
import PortableText from "../portableText";
import { cn } from "../../lib/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard, faArrowsTurnToDots, faCheckCircle, faExclamationTriangle,
  faMagnifyingGlass, faMoneyBill, faPersonDigging, faRobot, faSignal,
  faTimesCircle, faUsers, faUserSecret, faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import { SiteContext } from "../global/site-context";
import * as styles from "./compare-table.module.css";

const ICONS = {
  success: { icon: faCheckCircle, className: "text-green-emerald" },
  warn: { icon: faExclamationTriangle, className: "text-orange-400" },
  danger: { icon: faTimesCircle, className: "text-red" },
  signals: { icon: faSignal },
  identity: { icon: faAddressCard },
  prospect: { icon: faPersonDigging },
  automation: { icon: faRobot },
  price: { icon: faMoneyBill },
  "id-card": { icon: faAddressCard },
  "user-secret": { icon: faUserSecret },
  "magnifying-glass": { icon: faMagnifyingGlass },
  "wand-magic-sparkles": { icon: faWandMagicSparkles },
  "arrows-turn-to-dots": { icon: faArrowsTurnToDots },
  users: { icon: faUsers },
};

export const ComparisonTable = ({ section }) => {
  const site = useContext(SiteContext);
  const overviewMode = site.isHomepage;
  let dataRowIndex = 0;
  const emptyRowMode = false; //[null, ""].includes(section.table[0].native);

  return (
    <table className="w-full">
      <thead style={{ position: "relative" }}>
        <tr className="flex flex-wrap md:table-row">
          <td className="hidden md:table-cell p-4 align-top sticky top-0 z-10 bg-white">
            {section.header && (
              <h4 className="mb-4 text-2xl font-semibold leading-none sm:leading-tight tracking-tight">
                {section.header}
              </h4>
            )}
            <PortableText blocks={section._rawSummary} compact />
          </td>
          <td className="w-1/2 md:w-auto p-4 py-4 align-top sticky top-0 z-10 bg-gray-200 rounded-t-xl md:rounded-tr-none">
            <h4 className={cn(
              emptyRowMode ? "text-center" : "text-lg font-semibold mb-2"
            )}>
              {overviewMode ? section.competitor : "Common Room"}
            </h4>
            {(overviewMode ? section._rawCompetitorDescription : section._rawCrDescription) && (
              <PortableText
                blocks={overviewMode ? section._rawCompetitorDescription : section._rawCrDescription}
                compact
                className="text-sm leading-normal"
              />
            )}
          </td>
          <td className="w-1/2 md:w-auto bg-gray-200 rounded-t-xl p-4 py-4 align-top sticky top-0 z-10 md:rounded-tl-none">
            <h4 className={cn(
              emptyRowMode ? "text-center" : "text-lg font-semibold mb-2"
            )}>
              {overviewMode ? "Common Room + Zoom" : section.competitor}
            </h4>
            {(overviewMode ? section._rawCrDescription : section._rawCompetitorDescription) && (
              <PortableText
                blocks={overviewMode ? section._rawCrDescription : section._rawCompetitorDescription} compact
                className="text-sm leading-normal"
              />
            )}
          </td>
        </tr>
      </thead>
      <tbody className={cn(
        "align-top leading-normal rounded-xl",
        emptyRowMode ? "text-sm" : overviewMode ? "text-base" : "text-sm",
        overviewMode ? styles.overviewBody : null
      )}>
        {section.table.map((row, i) => {
          if (!row.isHeader) dataRowIndex++;
          const isAltRow = !row.isHeader && dataRowIndex % 2 === 0;
          const rowBg = !row.isHeader ? row.featured ? "bg-green-ultralight" : isAltRow ? "bg-dark-2 group-hover:bg-green-ultralight" : "bg-dark-5 group-hover:bg-green-ultralight" : null;

          return (
            <tr
              key={i}
              className={cn(
                "flex flex-wrap md:table-row group transition-colors",
                overviewMode ? null : "hover:bg-green-ultralight",
                row.isHeader || i === section.table.length - 1 ? "" : "border-b border-dark-10",
              )}
            >
              <td
                colSpan={row.isHeader ? 3 : 1}
                className={cn(
                  "w-full md:w-auto px-4 font-medium",
                  row.isHeader ? "text-lg font-semibold pt-8 pb-4" : "py-4",
                  overviewMode ? "uppercase text-dark-80 py-6" : null,
                  i === 0 ? "rounded-tl-xl" : null,
                  i === section.table.length - 1 ? "rounded-bl-xl" : null,
                  rowBg,
                )}
                style={row.featured ? { boxShadow: "inset 4px 0 0 #1BC88C" } : undefined}
              >
                {(row.isHeader && row.icon && ICONS[row.icon]) || site.isHomepage ? (
                  <span className="flex items-start gap-2">
                    <FontAwesomeIcon icon={ICONS[row.icon].icon}
                      className={cn(
                        "mt-0.5 shrink-0 text-green-emerald",
                        ICONS[row.icon].className
                      )}
                      style={{ height: "1.25rem" }}
                    />
                    {row.label}
                  </span>
                ) : row.url ? (
                  <a href={row.url} target="_blank" className="hover:underline">
                    {row.label}
                  </a>
                ) : (
                  row.label
                )}
                {row.featured && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full bg-mint text-mintDark text-micro font-semibold uppercase tracking-wide">
                    Key difference
                  </span>
                )}
                {/* {row._rawTooltip && (
                  <PortableText blocks={row._rawTooltip} />
                )} */}
              </td>
              {!row.isHeader && (
                <>
                  <td className={cn(
                    "w-1/2 md:w-auto p-4 pr-6",
                    overviewMode ? "py-6" : null,
                    rowBg
                  )}>
                    <div className={cn("flex", (overviewMode ? row.compare : row.native) ? "items-start" : "justify-center")}>
                      {!overviewMode ? <FontAwesomeIcon icon={faCheckCircle} size="lg" className="mr-2 mt-1 max-h-4 text-green-emerald" /> : null}
                      {overviewMode ? row.compare : row.native}
                    </div>
                  </td>
                  <td className={cn(
                    "w-1/2 md:w-auto p-4 leading-snug tracking-tight",
                    overviewMode ? "py-6" : null,
                    rowBg ?? "bg-white",
                    i === section.table.length - 1 ? "rounded-br-xl" : null,
                  )}>
                    {(overviewMode ? row.native : row.compare) || row.icon ? (
                      <div className={cn("flex", emptyRowMode ? "justify-center" : "items-start")}>
                        {(row.icon && ICONS[row.icon]) && !site.isHomepage ? (
                          <FontAwesomeIcon
                            icon={ICONS[row.icon].icon}
                            size="lg"
                            className={cn("mr-2 mt-0.5 max-h-4 shrink-0", ICONS[row.icon].className)}
                          />
                        ) : null}
                        {overviewMode ? row.native : row.compare}
                      </div>
                    ) : (
                      <div className="flex justify-center">
                        <FontAwesomeIcon icon={faTimesCircle} size="lg" className="text-red" />
                      </div>
                    )}
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
